import { useState } from 'react';

import { AlertCircle, ChevronDown, ChevronUp, Languages, Loader2, Wrench, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  ar: 'Arabic',
  de: 'German',
  es: 'Spanish',
  fr: 'French',
  it: 'Italian',
  nl: 'Dutch',
  pl: 'Polish',
  pt: 'Portuguese',
  ro: 'Romanian',
  tr: 'Turkish',
  ur: 'Urdu',
  hi: 'Hindi',
  zh: 'Chinese',
};

const languageLabel = (code?: string | null): string => {
  if (!code) return '';
  return LANGUAGE_NAMES[code.toLowerCase()] ?? code.toUpperCase();
};

type TranscriptMessage = {
  role: string;
  text?: string;
  content?: string;
  metadata?: {
    type?: string;
    toolName?: string;
    params?: Record<string, unknown>;
    attempt?: number;
    maxAttempts?: number;
  };
};

type ParsedTranscript = {
  messages: TranscriptMessage[];
  totalMessages: number;
};

const parseTranscript = (transcript: unknown): ParsedTranscript => {
  const source =
    transcript && typeof transcript === 'object' ? (transcript as Record<string, unknown>) : null;

  const rawMessages = Array.isArray(transcript)
    ? transcript
    : Array.isArray(source?.messages)
      ? source.messages
      : [];

  const messages = rawMessages
    .filter((msg): msg is TranscriptMessage => !!msg && typeof msg === 'object')
    .map((msg) => ({
      role: typeof msg.role === 'string' ? msg.role : 'assistant',
      text: typeof msg.text === 'string' ? msg.text : undefined,
      content: typeof msg.content === 'string' ? msg.content : undefined,
      metadata: msg.metadata && typeof msg.metadata === 'object' ? msg.metadata : undefined,
    }))
    .filter((msg) => Boolean(msg.text || msg.content));

  const totalMessages =
    typeof source?.totalMessages === 'number' && Number.isFinite(source.totalMessages)
      ? source.totalMessages
      : messages.length;

  return {
    messages,
    totalMessages,
  };
};

const SystemMessage = ({ message }: { message: TranscriptMessage }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = message.metadata;
  const isToolCall = meta?.type === 'tool_call';
  const isSilenceBreaker = meta?.type === 'silence_breaker';

  return (
    <div className="flex justify-center">
      <div className="border-border max-w-[90%] rounded-md border border-dashed px-3 py-1.5">
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          className="text-muted-foreground flex w-full items-center gap-1.5 text-xs"
        >
          {isToolCall ? (
            <Wrench className="h-3 w-3 shrink-0" />
          ) : isSilenceBreaker ? (
            <Zap className="h-3 w-3 shrink-0" />
          ) : (
            <AlertCircle className="h-3 w-3 shrink-0" />
          )}
          <span className="font-medium">
            {isToolCall
              ? `Tool: ${meta.toolName}`
              : isSilenceBreaker
                ? `Silence breaker ${meta.attempt}/${meta.maxAttempts}`
                : message.text || message.content}
          </span>
          {isToolCall &&
            meta.params &&
            Object.keys(meta.params).length > 0 &&
            (expanded ? (
              <ChevronUp className="ml-auto h-3 w-3 shrink-0" />
            ) : (
              <ChevronDown className="ml-auto h-3 w-3 shrink-0" />
            ))}
        </button>
        {expanded && isToolCall && meta.params && Object.keys(meta.params).length > 0 && (
          <div className="text-muted-foreground mt-1.5 border-t border-dashed pt-1.5 text-xs">
            {Object.entries(meta.params).map(([key, value]) => (
              <div key={key} className="flex gap-1">
                <span className="font-medium">{key}:</span>
                <span>{typeof value === 'string' ? value : JSON.stringify(value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface TranscriptSectionProps {
  transcript: unknown;
  enhancedTranscript: unknown;
  originalTranscript: unknown;
  hasEnhancedTranscriptFlag?: boolean;
  translatedTranscript?: unknown;
  translationLanguage?: string | null;
  detectedSourceLanguage?: string | null;
  onTranslate?: () => Promise<void>;
}

export const TranscriptSection = ({
  enhancedTranscript: rawEnhanced,
  originalTranscript: rawOriginal,
  hasEnhancedTranscriptFlag,
  translatedTranscript: rawTranslated,
  translationLanguage,
  detectedSourceLanguage,
  onTranslate,
}: TranscriptSectionProps) => {
  const enhancedTranscript = parseTranscript(rawEnhanced);
  const originalTranscript = parseTranscript(rawOriginal);
  const translatedTranscript = parseTranscript(rawTranslated);
  const hasEnhancedTranscript = enhancedTranscript.messages.length > 0;
  const hasOriginalTranscript = originalTranscript.messages.length > 0;
  const hasTranslatedTranscript = translatedTranscript.messages.length > 0;

  const [activeTranscriptTab, setActiveTranscriptTab] = useState<
    'translated' | 'enhanced' | 'original'
  >(hasTranslatedTranscript ? 'translated' : hasEnhancedTranscript ? 'enhanced' : 'original');
  const [translating, setTranslating] = useState(false);

  const activeTranscript =
    activeTranscriptTab === 'translated'
      ? translatedTranscript
      : activeTranscriptTab === 'enhanced'
        ? enhancedTranscript
        : originalTranscript;

  const handleTranslate = async () => {
    if (!onTranslate || translating) return;
    setTranslating(true);
    try {
      await onTranslate();
      setActiveTranscriptTab('translated');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          Call Transcript
          {(hasEnhancedTranscript || hasEnhancedTranscriptFlag) && (
            <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900 dark:text-purple-300">
              Enhanced
            </span>
          )}
          {hasTranslatedTranscript && detectedSourceLanguage && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              Translated from {languageLabel(detectedSourceLanguage)}
            </span>
          )}
        </CardTitle>
        <CardDescription>Switch between enhanced and original transcript sources.</CardDescription>
      </CardHeader>
      <CardContent>
        {hasEnhancedTranscript || hasOriginalTranscript || hasTranslatedTranscript ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {hasTranslatedTranscript && (
                <Button
                  type="button"
                  size="sm"
                  variant={activeTranscriptTab === 'translated' ? 'default' : 'outline'}
                  onClick={() => setActiveTranscriptTab('translated')}
                >
                  {languageLabel(translationLanguage) || 'Translated'} (
                  {translatedTranscript.totalMessages})
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant={activeTranscriptTab === 'enhanced' ? 'default' : 'outline'}
                onClick={() => setActiveTranscriptTab('enhanced')}
                disabled={!hasEnhancedTranscript}
              >
                Reviewed ({enhancedTranscript.totalMessages})
              </Button>
              <Button
                type="button"
                size="sm"
                variant={activeTranscriptTab === 'original' ? 'default' : 'outline'}
                onClick={() => setActiveTranscriptTab('original')}
                disabled={!hasOriginalTranscript}
              >
                Original ({originalTranscript.totalMessages})
              </Button>
              {!hasTranslatedTranscript && onTranslate && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleTranslate}
                  disabled={translating}
                >
                  {translating ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Languages className="mr-1 h-3 w-3" />
                  )}
                  Translate
                </Button>
              )}
            </div>

            <div className="text-muted-foreground border-b pb-3 text-xs">
              <span>{activeTranscript.totalMessages} messages</span>
            </div>

            {activeTranscript.messages.length > 0 ? (
              <div className="max-h-150 space-y-3 overflow-y-auto pr-2">
                {activeTranscript.messages.map((message, idx: number) =>
                  message.role === 'system' ? (
                    <SystemMessage key={idx} message={message} />
                  ) : (
                    <div
                      key={idx}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-4 py-2 ${
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground dark:text-white'
                            : 'bg-muted dark:text-white'
                        }`}
                      >
                        <span className="mb-1 text-xs font-semibold">
                          {message.role === 'user' ? 'Customer' : 'AI Agent'}
                        </span>
                        <p className="text-sm whitespace-pre-wrap">
                          {message.text || message.content}
                        </p>
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                No transcript messages available in this tab.
              </p>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No transcript available for this call.</p>
        )}
      </CardContent>
    </Card>
  );
};
